import { Nullable } from '@orcar/common';
import {
  Button,
  convertToKST,
  getHMFromNumber,
  getPrimaryColor,
  JEJURO,
  Modal,
  PAGE_NAME,
  SearchOption,
  SearchParamsType,
  useGetCompanyInfo,
} from '@orcar/common-d2c';
import dayjs from 'dayjs';
import { Dispatch, SetStateAction, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Dropdown } from '@/components/dropdown';
import { useSearchOptions } from '@/hooks/useSearchOptions';
import { DateSelection } from '@/pages/home/components/dateSelection/DateSelection';
import { DriverInfoSelection } from '@/pages/home/components/driverInfoSelection/DriverInfoSelection';
import {
  getAvailablePickUpTimeOptions,
  getRentalOperatingHours,
  getTimeOptions,
} from '@/utils/date';

type SelectModalProps = {
  initialSearchOptions: SearchParamsType;
  searchOptionId: Nullable<string>;
  setSearchOptionId: Dispatch<SetStateAction<Nullable<string>>>;
  onClose: () => void;
};

const STEPS = ['이용 일시', '운전자 정보'] as const;

const OptionChangeModal = ({
  searchOptionId,
  setSearchOptionId,
  onClose,
  initialSearchOptions,
}: SelectModalProps) => {
  const navigate = useNavigate();

  const getProducts = (params?: SearchParamsType) => {
    navigate({
      pathname: PAGE_NAME.search,
      search: new URLSearchParams({
        pickUpAt: convertToKST(params?.pickUpAt),
        dropOffAt: convertToKST(params?.dropOffAt),
        driverAge: params?.driverAge ? String(params.driverAge) : '',
        numberOfPeople: params?.numberOfPeople
          ? String(params.numberOfPeople)
          : '',
        drivingExperience: params?.drivingExperience
          ? String(params.drivingExperience)
          : '',
      }).toString(),
    });

    onClose();
  };

  const { searchOptions } = useSearchOptions(getProducts, initialSearchOptions);

  // 홈(HeroDateModal)과 동일한 컴팩트 시간 선택(드롭다운) 옵션
  const { data: companyData } = useGetCompanyInfo();
  const rentalOperatingHours = getRentalOperatingHours(companyData);
  const isToday = dayjs(convertToKST(searchOptions.pickUpDate?.format()))
    .tz('Asia/seoul')
    .isSame(dayjs().tz('Asia/seoul'), 'date');
  const pickUpOptions = isToday
    ? getAvailablePickUpTimeOptions(rentalOperatingHours)
    : getTimeOptions('pickUp', rentalOperatingHours);
  const dropOffOptions = searchOptions.pickUpDate?.isSame(
    searchOptions.dropOffDate,
    'date',
  )
    ? getTimeOptions('dropOff', rentalOperatingHours).filter(
        (option) => option.value > searchOptions.pickUpTimeValue,
      )
    : getTimeOptions('dropOff', rentalOperatingHours);

  // 운전자 정보 필드로 열었으면 2스텝부터, 아니면 1스텝(이용일시)부터
  const [step, setStep] = useState<1 | 2>(
    searchOptionId === SearchOption.DriverInfo ||
      searchOptionId === SearchOption.NumberOfPeople
      ? 2
      : 1,
  );

  const isDateTimeReady =
    !!searchOptions.pickUpDate &&
    !!searchOptions.dropOffDate &&
    !!searchOptions.pickUpTimeValue &&
    !!searchOptions.dropOffTimeValue;
  const isDriverReady =
    !!searchOptions.driverAge &&
    !!searchOptions.drivingExperience &&
    !!searchOptions.numberOfPeople;

  /** 마지막 스텝에서 날짜·시간·운전자 정보를 한 번에 적용 */
  const applyAll = () => {
    const pick = getHMFromNumber(searchOptions.pickUpTimeValue);
    const drop = getHMFromNumber(searchOptions.dropOffTimeValue);
    setSearchOptionId(null);
    getProducts({
      pickUpAt: searchOptions.pickUpDate
        ?.set('hour', pick.hh)
        .set('minute', pick.mm)
        .format(),
      dropOffAt: searchOptions.dropOffDate
        ?.set('hour', drop.hh)
        .set('minute', drop.mm)
        .format(),
      driverAge: searchOptions.driverAge?.value,
      numberOfPeople: searchOptions.numberOfPeople?.value,
      drivingExperience: searchOptions.drivingExperience?.value,
    } as SearchParamsType);
  };

  const handleFooter = () => {
    if (step === 1) {
      if (isDateTimeReady) setStep(2);
    } else if (isDriverReady) {
      applyAll();
    }
  };

  return (
    <Modal
      onClose={onClose}
      paddingHorizontal={28}
      paddingVertical={28}
      hasBorder={false}
      topLevel
    >
      <SearchModalWrapper>
        <ModalWrapper>
          <Stepper>
            {STEPS.map((label, index) => {
              const idx = (index + 1) as 1 | 2;
              return (
                <StepItem
                  key={label}
                  active={step === idx}
                  clickable={idx < step}
                  onClick={() => idx < step && setStep(idx)}
                >
                  <StepNo active={step === idx}>{idx}</StepNo>
                  <StepLabel active={step === idx}>{label}</StepLabel>
                </StepItem>
              );
            })}
          </Stepper>

          {step === 1 ? (
            <StepOne>
              <DateSelection
                pickUpDate={searchOptions.pickUpDate}
                setPickUpDate={searchOptions.setPickUpDate}
                dropOffDate={searchOptions.dropOffDate}
                setDropOffDate={searchOptions.setDropOffDate}
              />
              <TimeRow>
                <TimeField>
                  <TimeLabel>대여 시간</TimeLabel>
                  <Dropdown
                    width='100%'
                    options={pickUpOptions}
                    selectedValue={searchOptions.pickUpTimeValue}
                    onSelect={searchOptions.setPickUpTimeValue}
                  />
                </TimeField>
                <TimeField>
                  <TimeLabel>반납 시간</TimeLabel>
                  <Dropdown
                    width='100%'
                    options={dropOffOptions}
                    selectedValue={searchOptions.dropOffTimeValue}
                    onSelect={searchOptions.setDropOffTimeValue}
                  />
                </TimeField>
              </TimeRow>
            </StepOne>
          ) : (
            <SelectionWrapper>
              <DriverInfoSelection
                driverAge={searchOptions.driverAge}
                setDriverAge={searchOptions.setDriverAge}
                numberOfPeople={searchOptions.numberOfPeople}
                setNumberOfPeople={searchOptions.setNumberOfPeople}
                drivingExperience={searchOptions.drivingExperience}
                setDrivingExperience={searchOptions.setDrivingExperience}
              />
            </SelectionWrapper>
          )}
        </ModalWrapper>

        <Footer>
          <Button
            {...buttonStyle}
            onClick={handleFooter}
            disabled={step === 1 ? !isDateTimeReady : !isDriverReady}
          >
            {step === 1 ? '다음' : '설정'}
          </Button>
        </Footer>
      </SearchModalWrapper>
    </Modal>
  );
};

export default OptionChangeModal;

const buttonStyle = {
  width: '120px',
  height: '44px',
  margin: '0',
  borderRadius: '8px',
};

const ModalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const SearchModalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 840px;
  max-width: 90vw;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`;

const Stepper = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 20px;
`;

const StepItem = styled.div<{ active?: boolean; clickable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
  opacity: ${({ active }) => (active ? 1 : 0.5)};
`;

const StepNo = styled.span<{ active?: boolean }>`
  display: grid;
  place-items: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  ${({ theme }) => theme.FONT.B3};
  font-weight: 700;
  color: ${({ theme }) => theme.COLOR.WHITE};
  background: ${({ active, theme }) =>
    active ? getPrimaryColor(JEJURO) : theme.COLOR.GREY_03};
`;

const StepLabel = styled.span<{ active?: boolean }>`
  ${({ theme }) => theme.FONT.B2};
  font-weight: ${({ active }) => (active ? 700 : 500)};
`;

const StepOne = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
`;

const SelectionWrapper = styled.div`
  padding: 0 32px;
`;

const TimeRow = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  padding: 0 8px;
`;

const TimeField = styled.div`
  flex: 1;
  position: relative;
  height: 78px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TimeLabel = styled.span`
  ${({ theme }) => theme.FONT.B2};
  color: ${({ theme }) => theme.COLOR.GREY_02};
`;
